import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plane, Clock, Luggage, ArrowRight, Search } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../api';
import { Price } from '../context/CurrencyContext';
import { TravelBookingDialog } from '../components/TravelBookingDialog';

const fmtTime = (iso) => new Date(iso).toLocaleTimeString('en', { hour: '2-digit', minute: '2-digit', hour12: false });
const fmtDur = (m) => `${Math.floor(m / 60)}h ${m % 60}m`;
const today = new Date().toISOString().slice(0, 10);

const FlightCard = ({ o, onBook }) => (
  <Card className="border-0 shadow-md hover:shadow-xl transition-shadow" data-testid={`flight-offer-${o.offer_id}`}>
    <CardContent className="p-5 grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
      <div className="md:col-span-3 flex items-center gap-3">
        <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center text-white font-bold text-sm">{o.airline_code}</div>
        <div><p className="font-semibold text-gray-900">{o.airline}</p><p className="text-xs text-gray-500">{o.flight_number} · {o.cabin}</p></div>
      </div>
      <div className="md:col-span-5 flex items-center justify-between gap-3">
        <div className="text-center"><p className="text-xl font-bold">{fmtTime(o.departure)}</p><p className="text-xs text-gray-500">{o.origin}</p></div>
        <div className="flex-1 text-center">
          <p className="text-xs text-gray-500 flex items-center justify-center gap-1"><Clock className="w-3 h-3" />{fmtDur(o.duration_minutes)}</p>
          <div className="relative my-1 h-px bg-gray-300"><Plane className="w-4 h-4 text-teal-600 absolute -top-2 left-1/2 -translate-x-1/2 bg-white" /></div>
          <p className={`text-xs font-medium ${o.stops ? 'text-amber-600' : 'text-green-600'}`}>{o.stops ? `1 stop via ${o.via}` : 'Non-stop'}</p>
        </div>
        <div className="text-center"><p className="text-xl font-bold">{fmtTime(o.arrival)}</p><p className="text-xs text-gray-500">{o.destination}</p></div>
      </div>
      <div className="md:col-span-2 text-xs text-gray-500 space-y-1">
        <p className="flex items-center gap-1"><Luggage className="w-3 h-3" />{o.baggage}</p>
        <p>{o.refundable ? 'Refundable' : 'Non-refundable'}</p>
        <p className="text-rose-600">{o.seats_left} seats left</p>
      </div>
      <div className="md:col-span-2 text-right">
        <Price inr={o.total_price} className="block text-2xl font-bold text-teal-700" />
        <p className="text-xs text-gray-500 mb-2">{o.adults} adult{o.adults > 1 ? 's' : ''}</p>
        <Button data-testid={`flight-book-${o.offer_id}`} size="sm" onClick={() => onBook(o)} className="bg-gradient-to-r from-teal-500 to-cyan-600 text-white w-full">Book</Button>
      </div>
    </CardContent>
  </Card>
);

const Flights = ({ user, authChecked }) => {
  const navigate = useNavigate();
  const [airports, setAirports] = useState([]);
  const [q, setQ] = useState({ origin: 'KWI', destination: 'COK', date: '', adults: 1 });
  const [offers, setOffers] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  useEffect(() => { api.get('/travel/airports').then((r) => setAirports(r.data)).catch(() => {}); }, []);

  const search = async (e) => {
    e?.preventDefault();
    if (!q.date) { toast.error('Pick a departure date'); return; }
    if (q.origin === q.destination) { toast.error('Origin and destination must differ'); return; }
    setLoading(true);
    try {
      const res = await api.get('/travel/flights', { params: q });
      setOffers(res.data.offers);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Search failed');
    } finally { setLoading(false); }
  };

  const book = (o) => {
    if (!authChecked) return;
    if (!user) { toast.info('Login to book flights'); navigate('/login'); return; }
    setSelected(o);
  };

  const AirportSelect = ({ field, testId }) => (
    <Select value={q[field]} onValueChange={(v) => setQ((s) => ({ ...s, [field]: v }))}>
      <SelectTrigger data-testid={testId} className="border-2 h-11"><SelectValue /></SelectTrigger>
      <SelectContent>{airports.map((a) => <SelectItem key={a.code} value={a.code} data-testid={`${testId}-option-${a.code}`}>{a.code} — {a.city}</SelectItem>)}</SelectContent>
    </Select>
  );

  return (
    <div className="min-h-screen bg-gray-50 pt-16" data-testid="flights-page">
      <div className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl sm:text-5xl font-bold mb-2">Search Flights</h1>
          <p className="text-teal-50 text-lg">Compare fares to India & beyond — reserve now, pay on confirmation</p>
          <span className="inline-block mt-3 text-xs bg-white/20 rounded-full px-3 py-1" data-testid="sandbox-badge">Sandbox fares — live airline fares connect once provider keys are added</span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8">
        <Card className="border-0 shadow-xl">
          <CardContent className="p-5">
            <form onSubmit={search} className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end" data-testid="flight-search-form">
              <div className="md:col-span-3"><Label className="mb-1 block text-xs">From</Label><AirportSelect field="origin" testId="flight-origin" /></div>
              <div className="md:col-span-3"><Label className="mb-1 block text-xs">To</Label><AirportSelect field="destination" testId="flight-destination" /></div>
              <div className="md:col-span-3"><Label className="mb-1 block text-xs">Departure</Label><Input data-testid="flight-date" type="date" min={today} value={q.date} onChange={(e) => setQ((s) => ({ ...s, date: e.target.value }))} className="border-2 h-11" /></div>
              <div className="md:col-span-1"><Label className="mb-1 block text-xs">Adults</Label><Input data-testid="flight-adults" type="number" min={1} max={9} value={q.adults} onChange={(e) => setQ((s) => ({ ...s, adults: Number(e.target.value) || 1 }))} className="border-2 h-11" /></div>
              <Button data-testid="flight-search-btn" type="submit" disabled={loading} className="md:col-span-2 h-11 bg-gradient-to-r from-teal-500 to-cyan-600 text-white"><Search className="w-4 h-4 mr-2" />{loading ? 'Searching...' : 'Search'}</Button>
            </form>
          </CardContent>
        </Card>

        <div className="py-10 space-y-4" data-testid="flight-results">
          {offers === null ? (
            <p className="text-center text-gray-500 py-10">Enter your route and date to see available fares.</p>
          ) : offers.length === 0 ? (
            <p className="text-center text-gray-500 py-10" data-testid="flights-empty">No flights found for this route.</p>
          ) : (
            <>
              <p className="text-sm text-gray-500" data-testid="flights-count">{offers.length} fares · {q.origin} <ArrowRight className="inline w-3 h-3" /> {q.destination} · {q.date}</p>
              {offers.map((o) => <FlightCard key={o.offer_id} o={o} onBook={book} />)}
            </>
          )}
        </div>
      </div>

      {selected && <TravelBookingDialog open onOpenChange={(v) => !v && setSelected(null)} type="flight" offer={selected} count={selected.adults} user={user} />}
    </div>
  );
};

export default Flights;
